import { lang } from "i18nLang";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import {
  AccessProfileStore,
  ContractStore,
  MessageStore,
  ContractIntegrationStore as Store,
} from "store";
import DialogModal from "../UI/DialogModals";
import AutoCompleteField from "../UI/Fields/AutoCompleteField";
import { PageLoading } from "../UI/Page";

interface IProps {
  /** Propriedade que indica se o modal está aberto ou fechado. */
  open: boolean;
  /** Função que será chamada ao fechar o modal */
  onClose: () => void;
}

/** Formulário para vincular um Orçamento do ERP Mega ao contrato atual.
 * @category Components
 */
const ContractExecutiveBudget = observer(({ open, onClose }: IProps) => {
  const [loading, setLoading] = useState(false);
  const [loadingBudget, setLoadingBudget] = useState(false);

  const [branch, setBranch] = useState(null as any);
  const [budget, setBudget] = useState(null as any);

  useEffect(() => {
    getCurrentData();
    if (open) {
      if (Store.isAuthenticated) {
        setLoading(true);
        Store.getDataToExecutiveBudget()
          .then(() => {
            getCurrentData();
          })
          .catch((error) => {
            console.error(error);
            MessageStore.error(lang("contract.errors.fetchData"));
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        if (AccessProfileStore.currentUserIsAdmin()) {
          MessageStore.openIntegrationMessage();
        } else {
          MessageStore.error(lang("integrationSetup.error.missIntegration"));
        }
      }
    }
  }, [open]);

  function getCurrentData() {
    const contract = ContractStore.getCurrentContract();
    if (contract) {
      const branchId = contract.executiveBudget?.branchId || contract.branchId;
      const currentBranch = Store.findBranch(branchId || 0);

      const budgetId = contract.executiveBudget?.id;
      const currentBudget = Store.findBudget(budgetId || "");

      setBranch(currentBranch);
      setBudget(currentBudget);
    }
  }

  const handleConfirm = () => {
    ContractStore.updateExecutiveBudgetAndRefreshIndicators({
      branchId: branch?.id,
      budgetId: budget?.id,
    });
  };

  const handleChangeBranch = (name, value: any) => {
    setBranch(value);
    fetchBudget(value?.id);
  };

  const handleChangeBudget = (name, value: any) => setBudget(value);

  const fetchBudget = (branchId) => {
    setBudget(null);
    setLoadingBudget(true);
    Store.fetchAndSetBudgets(branchId).finally(() => setLoadingBudget(false));
  };

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title={lang("contract.executiveBudget.title")}
      confirmLabel={lang("link")}
      onConfirm={handleConfirm}
    >
      {loading && <PageLoading style={{ padding: "2rem 0" }} />}
      {!loading && (
        <>
          <AutoCompleteField
            name="branch"
            label={lang("contract.executiveBudget.fields.branch")}
            options={Store.branchs}
            fullWidth
            value={branch}
            onChangeOption={handleChangeBranch}
            InputProps={{
              margin: "normal",
            }}
          />
          <AutoCompleteField
            name="budget"
            label={lang("contract.executiveBudget.fields.budget")}
            options={Store.budgets}
            fullWidth
            value={budget}
            onChangeOption={handleChangeBudget}
            loading={loadingBudget}
            InputProps={{
              margin: "normal",
            }}
          />
        </>
      )}
    </DialogModal>
  );
});

export default ContractExecutiveBudget;
